import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

const QuoteForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    projectType: '',
    projectSize: '',
    location: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, projectType: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent(`Quote Request from ${formData.companyName}`);
    const body = encodeURIComponent(
      `New Quote Request:\n\n` +
      `Company: ${formData.companyName}\n` +
      `Contact Person: ${formData.contactName}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Project Type: ${formData.projectType}\n` +
      `Project Size: ${formData.projectSize} m²\n` +
      `Location: ${formData.location}\n\n` +
      `Message:\n${formData.message}`
    );
    
    // Open default email client
    window.location.href = `mailto:charterroofings@gmail.com?subject=${subject}&body=${body}`;
    
    toast.success(t.quote.success);
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      projectType: '',
      projectSize: '',
      location: '',
      message: '',
    });
  };

  return (
    <section id="quote" className="py-24 bg-[#0f172a]">
      <div className="container mx-auto px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-[#60a5fa] mb-4">{t.quote.title}</h2>
            <p className="body-large text-[#94a3b8]">{t.quote.subtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-[#1e293b] p-8 border border-[#1e40af]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-[#60a5fa]">
                  {t.quote.form.companyName} *
                </Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName" className="text-[#60a5fa]">
                  {t.quote.form.contactName} *
                </Label>
                <Input
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#60a5fa]">
                  {t.quote.form.email} *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#60a5fa]">
                  {t.quote.form.phone} *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectType" className="text-[#60a5fa]">
                  {t.quote.form.projectType} *
                </Label>
                <Select onValueChange={handleSelectChange} value={formData.projectType} required>
                  <SelectTrigger className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]">
                    <SelectValue placeholder={t.quote.form.projectType} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f172a] border-[#1e40af]">
                    {t.quote.form.projectTypes.map((type, index) => (
                      <SelectItem key={index} value={type} className="text-[#60a5fa] focus:bg-[#1e293b] focus:text-[#60a5fa]">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectSize" className="text-[#60a5fa]">
                  {t.quote.form.projectSize}
                </Label>
                <Input
                  id="projectSize"
                  name="projectSize"
                  value={formData.projectSize}
                  onChange={handleChange}
                  placeholder="e.g., 5000"
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
                />
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <Label htmlFor="location" className="text-[#60a5fa]">
                {t.quote.form.location} *
              </Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="City, Netherlands"
                className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
              />
            </div>

            <div className="space-y-2 mb-6">
              <Label htmlFor="message" className="text-[#60a5fa]">
                {t.quote.form.message}
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa] focus:border-[#60a5fa]"
              />
            </div>

            <Button type="submit" className="btn-primary w-full flex items-center justify-center space-x-2">
              <span>{t.quote.form.submit}</span>
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
